import classNames from "classnames";
import { useCallback, useEffect } from "react";

import { useNotificationChannel } from "@/hooks/useNotificationChannel";
import type { Notification } from "@/types";
import { useReadNotification } from "@/user/services/notifications";

import UserNotificationItem from "./UserNotificationItem";

export default function UserNotifications() {
  const { notifications, setNotifications } = useNotificationChannel();
  const readNotification = useReadNotification();

  const handleReadNotification = useCallback(
    (notification: Notification) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      readNotification.mutate(notification.id);
    },
    [readNotification, setNotifications],
  );

  useEffect(() => {
    if (notifications.length > 0) {
      const lastNotification = notifications[notifications.length - 1];

      const timerRef = setTimeout(() => {
        setNotifications((prev) =>
          prev.filter((n) => n.id !== lastNotification.id),
        );
      }, 15000);

      return () => {
        clearTimeout(timerRef);
      };
    }
  }, [notifications, setNotifications]);

  if (!notifications || notifications.length === 0) {
    return null;
  }

  const displayNotifications = [...notifications].slice(-3).reverse();

  return (
    <div className="w-full overflow-hidden rounded-xl pb-7">
      <div className="relative flex w-full flex-col items-end">
        {displayNotifications.map((notification, index) => (
          <UserNotificationItem
            key={notification.id}
            notification={notification}
            className={classNames({
              "relative z-30 bg-white": index % 3 === 0,
              "absolute -bottom-3 z-20 scale-95 bg-gray-50": index % 3 === 1,
              "absolute -bottom-6 z-10 scale-90 bg-gray-200": index % 3 === 2,
            })}
            onRequestHide={() => {
              handleReadNotification(notification);
            }}
          />
        ))}
      </div>
    </div>
  );
}
